// "..." dropdown menu that sits next to the primary "Create X" button on list pages.
// Parent page controls what action items to render and what happens when clicked.
import { useEffect, useRef, useState } from "react";
import { MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";

export interface BulkActionItem {
  label: string;
  icon?: React.ReactNode;
  /** Hide/disable based on selection count. 0 = enabled always, n>0 = requires >= n selected. */
  requiresSelection?: number;
  /** Action handler. */
  onClick: () => void;
  danger?: boolean;
  /** Show a horizontal rule above this item. */
  divideAbove?: boolean;
}

interface Props {
  selectedCount: number;
  items: BulkActionItem[];
}

export function BulkActionMenu({ selectedCount, items }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <Button
        variant="outline"
        size="icon"
        aria-label="Bulk actions"
        onClick={() => setOpen((o) => !o)}
      >
        <MoreHorizontal className="h-4 w-4" />
      </Button>
      {open && (
        <div className="absolute right-0 top-full z-20 mt-1 w-52 rounded-md border bg-popover py-1 text-sm shadow-lg">
          {selectedCount > 0 && (
            <div className="border-b px-3 py-1.5 text-xs text-muted-foreground">
              {selectedCount} selected
            </div>
          )}
          {items.map((item, i) => {
            const disabled = (item.requiresSelection ?? 0) > selectedCount;
            return (
              <div key={i}>
                {item.divideAbove && <div className="my-1 border-t" />}
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => { if (!disabled) { setOpen(false); item.onClick(); } }}
                  className={cn(
                    "flex w-full items-center gap-2 px-3 py-1.5 text-left",
                    disabled ? "text-muted-foreground/50 cursor-not-allowed" :
                               item.danger ? "text-destructive hover:bg-muted" :
                                             "hover:bg-muted",
                  )}
                >
                  {item.icon}
                  {item.label}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
